import { useState } from "react";
import { Leaf, LogOut, LayoutDashboard, Camera, Sparkles, Crown, Menu, X } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { NOTCH } from "@/constants/testIds";

export default function AppHeader() {
  const { user, logout } = useAuth();
  const loc = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const links = [
    { to: "/", active: loc.pathname === "/", icon: <Sparkles size={14} />, label: "New Plan" },
    { to: "/dashboard", active: loc.pathname === "/dashboard", icon: <LayoutDashboard size={14} />, label: "Your Plans", testid: NOTCH.navDashboard },
    { to: "/photolog", active: loc.pathname.startsWith("/photolog"), icon: <Camera size={14} />, label: "Photo log", testid: NOTCH.navPhotoLog },
    { to: "/pricing", active: loc.pathname === "/pricing", icon: <Crown size={14} />, label: "Pro", testid: NOTCH.navPricing },
  ];

  return (
    <header className="max-w-6xl mx-auto px-4 sm:px-8 pt-6 sm:pt-8 pb-2 relative">
      <div className="flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid={NOTCH.appBrand}>
          <div className="h-8 w-8 rounded-full bg-[#2C4C3B] flex items-center justify-center">
            <Leaf size={16} className="text-[#F9F8F6]" />
          </div>
          <span className="font-serif-display text-2xl tracking-tight text-[#2C4C3B]">Notch</span>
        </Link>

        <nav className="flex items-center gap-1 sm:gap-2">
          {user && user !== false && (
            <>
              {links.map((l) => (
                <NavLink key={l.to} {...l} />
              ))}
              <button
                data-testid={NOTCH.navLogout}
                onClick={logout}
                className="hidden sm:inline-flex ml-2 items-center gap-1 px-3 py-1.5 rounded-full text-xs text-[#666] hover:text-[#2C4C3B] hover:bg-[#F2EFE9] transition-colors"
                title="Sign out"
              >
                <LogOut size={13} />
                <span>Sign out</span>
              </button>
              <button
                onClick={() => setMobileOpen((v) => !v)}
                className="sm:hidden inline-flex items-center justify-center h-9 w-9 rounded-full text-[#2C4C3B] hover:bg-[#F2EFE9] transition-colors"
                aria-label={mobileOpen ? "Close menu" : "Open menu"}
              >
                {mobileOpen ? <X size={20} /> : <Menu size={20} />}
              </button>
            </>
          )}
          {user === false && (
            <Link to="/auth" className="text-sm text-[#2C4C3B] font-medium hover:underline">Sign in</Link>
          )}
        </nav>
      </div>

      {mobileOpen && user && user !== false && (
        <div className="sm:hidden absolute left-4 right-4 top-full mt-2 bg-white rounded-[1.5rem] border border-[#E5E2DC] shadow-[0_20px_60px_-20px_rgba(0,0,0,0.2)] p-3 z-50 flex flex-col gap-1">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={l.testid}
              onClick={() => setMobileOpen(false)}
              className={`flex items-center gap-2.5 px-4 py-3 rounded-2xl text-sm transition-colors ${
                l.active ? "bg-[#2C4C3B] text-white" : "text-[#444] hover:bg-[#F2EFE9]"
              }`}
            >
              {l.icon}{l.label}
            </Link>
          ))}
          <button
            onClick={() => { setMobileOpen(false); logout(); }}
            className="flex items-center gap-2.5 px-4 py-3 rounded-2xl text-sm text-[#666] hover:bg-[#F2EFE9] transition-colors"
          >
            <LogOut size={14} /> Sign out
          </button>
        </div>
      )}
    </header>
  );
}

function NavLink({ to, active, icon, label, testid }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      className={`hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs transition-colors ${
        active ? "bg-[#2C4C3B] text-white" : "text-[#666] hover:text-[#2C4C3B] hover:bg-[#F2EFE9]"
      }`}
    >
      {icon}{label}
    </Link>
  );
}
